import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiagramProject } from '@fortawesome/free-solid-svg-icons';
import Alert from '../../../components/common/Alert';
import Spinner from '../../../components/common/Spinner';
import Modal from '../../../components/common/Modal';
import BpmnModelerEditor from '../../../components/bpm/modeler/BpmnModelerEditor';
import BpmModelerService from '../../../services/BpmModelerService';
import BpmGatewayService from '../../../services/BpmGatewayService';

const sanitizeModelerKey = (name = '') => {
  let result = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  result = result.toLowerCase();
  result = result.replace(/[^a-z0-9]+/g, '_');
  result = result.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return result;
};

const BpmModelerEditor = ({ modelerId = null, embedded = false, onClose = null, onDataChanged = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idFromQuery = query.get('id');
  const id = modelerId ?? idFromQuery;

  const [loading, setLoading] = useState(!!id);
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [xmlContent, setXmlContent] = useState('');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [versionAction, setVersionAction] = useState('REPLACE');
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const modelerEditorRef = useRef(null);
  const [formData, setFormData] = useState({
    id: null,
    modelerName: '',
    modelerKey: '',
    version: '',
    isDeployment: false,
    deploymentId: null,
    processDefinitionId: null,
    processDefinitionKey: null,
  });
  const currentVersion = versions.find((v) => String(v.versionId || v.id) === String(selectedVersionId)) || formData;
  const isCurrentPublished = Boolean(currentVersion?.isDeployment);
  const hasPersistedModeler = Boolean(formData?.id || selectedVersionId || (versions && versions.length));

  const loadVersionsByKey = async (modelerKey, preferredId = null) => {
    const list = await BpmModelerService.getAllByKey(modelerKey);
    setVersions(list || []);
    if (preferredId) {
      setSelectedVersionId(String(preferredId));
    } else if (list && list.length > 0) {
      setSelectedVersionId(String(list[0].versionId || list[0].id));
    }
  };

  const loadModeler = async (modelerId) => {
    const modeler = await BpmModelerService.getById(modelerId);
    setFormData({
      id: modeler?.id || null,
      modelerName: modeler?.modelerName || '',
      modelerKey: modeler?.modelerKey || '',
      version: modeler?.version || '',
      isDeployment: modeler?.isDeployment ?? false,
      deploymentId: modeler?.deploymentId || null,
      processDefinitionId: modeler?.processDefinitionId || null,
      processDefinitionKey: modeler?.processDefinitionKey || null,
    });
    setXmlContent(modeler?.xmlContent || '');
    if (modeler?.modelerKey) {
      await loadVersionsByKey(modeler.modelerKey, modeler.versionId || null);
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    loadModeler(id)
      .catch((err) => {
        setError(err.message || t('common.error'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVersionChange = async (versionId) => {
    if (!versionId) {
      return;
    }
    setSelectedVersionId(versionId);
    setIsChangingVersion(true);
    try {
      const version = await BpmModelerService.getVersionById(versionId);
      setFormData((prev) => ({
        ...prev,
        id: version?.modelerId || prev.id || null,
        modelerName: version?.modelerName || prev.modelerName || '',
        modelerKey: version?.modelerKey || prev.modelerKey || '',
        version: version?.version || '',
        isDeployment: version?.isDeployment ?? false,
        deploymentId: version?.deploymentId || null,
        processDefinitionId: version?.processDefinitionId || null,
        processDefinitionKey: version?.processDefinitionKey || null,
      }));
      setXmlContent(version?.xmlContent || '');
    } catch (err) {
      setError(err.message || t('common.error'));
    } finally {
      setIsChangingVersion(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!formData.modelerName?.trim()) {
      setError(t('bpmModeler.errors.modelerNameRequired'));
      return false;
    }

    if (!formData.modelerKey?.trim()) {
      setError(t('bpmModeler.errors.modelerKeyRequired'));
      return false;
    }

    setSaving(true);
    try {
      const currentVersionItem = versions.find((v) => String(v.versionId || v.id) === String(selectedVersionId)) || formData;
      const replaceCurrentVersion = versionAction === 'REPLACE';
      const currentXml = await modelerEditorRef.current?.exportXmlForSave?.(formData.modelerKey.trim());

      const payload = {
        id: formData.id || undefined,
        versionId: replaceCurrentVersion ? (currentVersionItem?.versionId || currentVersionItem?.id || null) : null,
        modelerName: formData.modelerName.trim(),
        modelerKey: formData.modelerKey.trim(),
        xmlContent: currentXml || xmlContent || '',
        isDeployment: formData.isDeployment ?? false,
        replaceCurrentVersion,
        ...(hasPersistedModeler && currentVersionItem?.version ? { version: currentVersionItem.version } : {}),
      };

      const saved = await BpmModelerService.create(payload);
      if (!saved || !saved.id) {
        throw new Error(t('bpmModeler.errors.saveNoPersisted') || 'No se pudo guardar el modelador.');
      }
      setSuccess(t('bpmModeler.success.saved'));
      await loadModeler(saved.id);
      if (onDataChanged) {
        onDataChanged();
      }
      return true;
    } catch (err) {
      if (err?.isAuthError) {
        setError(t('bpmModeler.errors.unauthorized') || 'No autorizado para guardar este modelador.');
      } else {
        setError(err.message || t('common.error'));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openSaveModal = () => {
    setError('');
    setSuccess('');
    setVersionAction(hasPersistedModeler ? 'REPLACE' : 'NEW');
    setIsSaveModalOpen(true);
  };

  const handleDeploy = async () => {
    if (!selectedVersionId) {
      setError(t('bpmModeler.errors.selectVersionToDeploy'));
      return;
    }

    setDeploying(true);
    setError('');
    setSuccess('');
    try {
      await BpmGatewayService.publishBpmn(selectedVersionId);
      setSuccess(t('bpmModeler.success.deployed'));
      await loadModeler(selectedVersionId);
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (err) {
      if (err?.isAuthError) {
        setError(t('bpmModeler.errors.unauthorized') || 'No autorizado para publicar este proceso.');
      } else {
        setError(err.message || t('common.error'));
      }
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: '#fff', zIndex: 3000 }}>
        <Spinner overlay={true} size="large" message={t('common.loading')} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#fff',
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <h4 style={{ margin: 0 }}>
          <FontAwesomeIcon icon={faDiagramProject} /> {t('bpmModeler.editorTitle')}
          {formData?.modelerName ? ` - ${formData.modelerName}` : ''}
          {formData?.version ? ` - ${t('bpmModeler.version')}: ${formData.version}` : ''}
        </h4>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          style={{
            minWidth: 34,
            height: 34,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
          }}
          onClick={() => {
            if (embedded && onClose) {
              onClose();
              return;
            }
            navigate('/bpm/modeler');
          }}
          aria-label={t('common.close') || 'Cerrar'}
        >
          <i className="fa fa-times" style={{ fontSize: 14 }}></i>
        </button>
      </div>

      <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
          <Alert type="error" message={error} onClose={() => setError('')} autoClose={5000} />
          <Alert type="success" message={success} onClose={() => setSuccess('')} autoClose={4000} />

          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {!!versions.length && (
              <div style={{ minWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0, whiteSpace: 'nowrap' }}>{t('bpmModeler.selectedVersion')}</label>
                <select
                  className="form-control"
                  value={selectedVersionId}
                  disabled={isChangingVersion}
                  onChange={(e) => handleVersionChange(e.target.value)}
                  style={{ minWidth: 220 }}
                >
                  <option value="">{t('common.select')}</option>
                  {versions.map((version) => (
                    <option key={version.versionId || version.id} value={version.versionId || version.id}>
                      {version.version || '-'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!!versions.length && (
              <div style={{ minWidth: 140, display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '6px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    color: isCurrentPublished ? '#0f5132' : '#664d03',
                    backgroundColor: isCurrentPublished ? '#d1e7dd' : '#fff3cd',
                    border: `1px solid ${isCurrentPublished ? '#badbcc' : '#ffecb5'}`,
                  }}
                >
                  {isCurrentPublished ? (t('bpmModeler.published') || 'Publicado') : (t('bpmModeler.draft') || 'Draft')}
                </div>
              </div>
            )}
            <div style={{ minWidth: 280, flex: '0 1 auto', display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-primary" onClick={openSaveModal} disabled={saving}>
                <FontAwesomeIcon icon={faDiagramProject} /> {saving ? t('common.processing') : t('common.save')}
              </button>
              <button type="button" className="btn btn-success" onClick={handleDeploy} disabled={deploying || !selectedVersionId}>
                <i className="fa fa-upload"></i> {deploying ? t('common.processing') : t('bpmModeler.publish')}
              </button>
            </div>
          </div>

          {isChangingVersion && <Spinner overlay={true} size="small" message={t('common.loading')} />}
          <BpmnModelerEditor ref={modelerEditorRef} xml={xmlContent} onXmlChange={setXmlContent} />
      </div>

      <Modal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        title={t('bpmModeler.saveModalTitle') || 'Guardar modelador'}
        icon={faDiagramProject}
      >
        <div className="form-group">
          <label>{t('bpmModeler.modelerName')}</label>
          <input
            type="text"
            className="form-control"
            value={formData.modelerName}
            onChange={(e) => {
              const modelerName = e.target.value;
              setFormData((prev) => ({
                ...prev,
                modelerName,
                modelerKey: sanitizeModelerKey(modelerName),
              }));
            }}
          />
        </div>
        <div className="form-group">
          <label>{t('bpmModeler.modelerKey')}</label>
          <input
            type="text"
            className="form-control"
            value={formData.modelerKey}
            onChange={(e) => setFormData((prev) => ({ ...prev, modelerKey: e.target.value }))}
          />
        </div>
        {hasPersistedModeler && (
          <div className="form-group">
            <label style={{ display: 'block', textAlign: 'center', marginBottom: 10 }}>
              {t('bpmModeler.versioning') || 'Versionado'}
            </label>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 24,
                width: '100%',
                flexWrap: 'nowrap',
              }}
            >
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  margin: 0,
                  gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="radio"
                  name="versionAction"
                  value="REPLACE"
                  checked={versionAction === 'REPLACE'}
                  onChange={(e) => setVersionAction(e.target.value)}
                />
                {t('bpmModeler.versionReplace')}
              </label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  margin: 0,
                  gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="radio"
                  name="versionAction"
                  value="NEW"
                  checked={versionAction === 'NEW'}
                  onChange={(e) => setVersionAction(e.target.value)}
                />
                {t('bpmModeler.versionNew')}
              </label>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setIsSaveModalOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              const ok = await handleSave();
              if (ok) {
                setIsSaveModalOpen(false);
              }
            }}
            disabled={saving}
          >
            {saving ? t('common.processing') : t('common.save')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BpmModelerEditor;
